// Utilities for UPC-A, EAN-13, EAN-8 validation and normalization

export function digitsOnly(value) {
  return String(value || '').replace(/\D+/g, '');
}

// Check digit calculations per standards
export function computeUPCACheckDigit(digits11) {
  const d = digitsOnly(digits11).padStart(11, '0').slice(-11);
  if (d.length !== 11) return null;
  let sumOdd = 0; // positions 1,3,5,7,9,11 (1-indexed)
  let sumEven = 0; // positions 2,4,6,8,10 (1-indexed)
  for (let i = 0; i < 11; i++) {
    const n = d.charCodeAt(i) - 48;
    if ((i % 2) === 0) sumOdd += n; else sumEven += n; // i=0 => pos1 (odd)
  }
  const total = sumOdd * 3 + sumEven;
  const mod = total % 10;
  return String(mod === 0 ? 0 : 10 - mod);
}

export function computeEAN13CheckDigit(digits12) {
  const d = digitsOnly(digits12).padStart(12, '0').slice(-12);
  if (d.length !== 12) return null;
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const n = d.charCodeAt(i) - 48;
    sum += (i % 2 === 0) ? n : n * 3; // weights 1,3 alternating starting with 1
  }
  const mod = sum % 10;
  return String(mod === 0 ? 0 : 10 - mod);
}

export function computeEAN8CheckDigit(digits7) {
  const d = digitsOnly(digits7).padStart(7, '0').slice(-7);
  if (d.length !== 7) return null;
  // Weights: 3,1 alternating starting from the rightmost of the data part
  // Practical approach: left-to-right weights [3,1,3,1,3,1,3]
  const weights = [3, 1, 3, 1, 3, 1, 3];
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    const n = d.charCodeAt(i) - 48;
    sum += n * weights[i];
  }
  const mod = sum % 10;
  return String(mod === 0 ? 0 : 10 - mod);
}

export function validateUPC12(digits) {
  const d = digitsOnly(digits);
  if (d.length !== 12) return false;
  const check = computeUPCACheckDigit(d.slice(0, 11));
  return check === d.slice(11);
}

export function validateEAN13(digits) {
  const d = digitsOnly(digits);
  if (d.length !== 13) return false;
  const check = computeEAN13CheckDigit(d.slice(0, 12));
  return check === d.slice(12);
}

export function validateEAN8(digits) {
  const d = digitsOnly(digits);
  if (d.length !== 8) return false;
  const check = computeEAN8CheckDigit(d.slice(0, 7));
  return check === d.slice(7);
}

// Validate and classify 8/12/13-digit codes for EAN/UPC
export function validateAndClassify(digits) {
  const d = digitsOnly(digits);
  if (d.length === 8) return validateEAN8(d) ? { valid: true, type: 'EAN8', digits: d } : { valid: false, type: 'EAN8', digits: d };
  if (d.length === 12) return validateUPC12(d) ? { valid: true, type: 'UPC', digits: d } : { valid: false, type: 'UPC', digits: d };
  if (d.length === 13) return validateEAN13(d) ? { valid: true, type: 'EAN13', digits: d } : { valid: false, type: 'EAN13', digits: d };
  return { valid: false, type: null, digits: d };
}

export function barcodeFormatFor(digits) {
  const d = digitsOnly(digits);
  if (d.length === 8) return 'EAN8';
  if (d.length === 12) return 'UPC';
  if (d.length === 13) return 'EAN13';
  return null;
}

export function preventNonDigitsOnKeyDown(e) {
  const allowedKeys = [
    'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End',
  ];
  if (e.ctrlKey || e.metaKey) return; // allow copy/paste/select-all shortcuts
  if (allowedKeys.includes(e.key)) return;
  if (/^\d$/.test(e.key)) return;
  e.preventDefault();
}

export function handlePasteDigitsOnly(e, onValue) {
  const text = (e.clipboardData || window.clipboardData).getData('text');
  const d = digitsOnly(text);
  e.preventDefault();
  if (typeof onValue === 'function') onValue(d);
}


